import { useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { type BusinessSubscription } from "lens-platform-sdk";

import { type CanceledSeat, isCanceledSeat } from "src/services/getSubscriptionSeatState";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useTrackedTableSort } from "src/hooks/useTrackedTableSort";

import { AvatarTableCell } from "src/components/TableCells/AvatarTableCell";
import DateCell, { dateColDef } from "src/components/TableCells/DateCell";
import Table from "src/components/Table/Table";
import { subscriptionColDef } from "src/components/TableCells/SubscriptionCell";

import styles from "src/components/Business/CanceledSeatTable.module.css";

const canceledTableColumnHelper = createColumnHelper<CanceledSeat>();

const canceledColumns = [
  canceledTableColumnHelper.accessor("user", {
    header: () => <span>Lens ID</span>,
    cell: ({ getValue }) => {
      const user = getValue();

      return <AvatarTableCell name={user?.username ?? ""} subtitle={user?.fullname} />;
    },
    meta: { primary: true },
  }),
  canceledTableColumnHelper.accessor("subscription", { ...subscriptionColDef }),
  canceledTableColumnHelper.accessor("subscription.currentPeriodEndsAt", {
    ...dateColDef,
    header: () => <span>Subscription Valid Until</span>,
    cell: ({ getValue }) => <DateCell getValue={getValue} className={styles.validUntil} />,
  }),
  canceledTableColumnHelper.accessor("deactivatedAt", {
    ...dateColDef,
    header: () => <span>Deactivation Date</span>,
    cell: ({ getValue }) => <DateCell getValue={getValue} className={styles.deactivatedAt} />,
  }),
  canceledTableColumnHelper.accessor("offline", {
    header: () => <span>Offline</span>,
    cell: ({ getValue }) => (
      <div className={styles.offlineContainer}>
        {getValue() && <span title="Seat used offline" className={styles.offline} />}
      </div>
    ),
  }),
];

const caption = "Canceled seats";

interface Props {
  businessSubscriptions: BusinessSubscription[];
}

const CanceledSeatTable = ({ businessSubscriptions }: Props) => {
  const platformClient = useLensPlatformClient();
  const canceledTableData = useMemo<CanceledSeat[]>(() => {
    const useSeats = businessSubscriptions
      .map(({ usedSeats }) => usedSeats)
      .flat()
      .filter(isCanceledSeat);

    return useSeats.map((usedSeat) => ({
      ...usedSeat,
      subscription: businessSubscriptions.find(({ id }) => id === usedSeat.subscriptionId),
      user: usedSeat.user
        ? {
            ...usedSeat.user,
            fullname: usedSeat.user ? platformClient.user.getUserFullName(usedSeat.user) : "",
          }
        : null,
    }));
  }, [businessSubscriptions, platformClient.user]);
  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const canceledTable = useReactTable({
    data: canceledTableData,
    columns: canceledColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange,
  });
  const canceledTableRowModel = canceledTable.getRowModel();
  const canceledTableRows = useMemo(() => canceledTableRowModel.rows, [canceledTableRowModel]);

  return (
    <Table<CanceledSeat>
      caption={caption}
      rows={canceledTableRows}
      columns={canceledTable.getAllColumns()}
      headerGroups={canceledTable.getHeaderGroups()}
      rowProps={{
        cellClassName: styles.tableCell,
      }}
      noDataText="No canceled seats on your Lens Business ID!"
    />
  );
};

export default CanceledSeatTable;
